import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaClock, FaTag, FaTimes, FaCheckCircle } from 'react-icons/fa';
import './Blog.css';

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  const blogPosts = [
    {
      title: 'How to Write Effective Test Cases',
      excerpt: 'Learn the essential elements of writing clear, comprehensive, and maintainable test cases that ensure complete test coverage.',
      date: 'Dec 15, 2025',
      readTime: '5 min read',
      category: 'Best Practices',
      tags: ['Test Cases', 'Documentation', 'QA Fundamentals'],
      color: '#3b82f6',
      content: {
        introduction: 'Writing effective test cases is a fundamental skill for any QA professional. Well-written test cases ensure comprehensive coverage, facilitate efficient testing, and serve as valuable documentation for the entire team.',
        sections: [
          {
            heading: 'Essential Components of a Test Case',
            points: [
              'Test Case ID: Unique identifier for tracking and reference',
              'Test Description: Clear, concise summary of what is being tested',
              'Preconditions: Required setup or state before test execution',
              'Test Steps: Detailed, numbered steps to execute the test',
              'Expected Results: Clear definition of what constitutes a pass',
              'Actual Results: Space to document what actually occurred',
              'Status: Pass/Fail/Blocked indicator'
            ]
          },
          {
            heading: 'Best Practices for Test Case Writing',
            points: [
              'Keep test cases atomic - test one thing at a time',
              'Use clear, unambiguous language that anyone can understand',
              'Make test cases independent - no dependencies on other tests',
              'Include both positive and negative test scenarios',
              'Maintain traceability to requirements',
              'Review and update test cases regularly',
              'Use consistent naming conventions and formatting'
            ]
          },
          {
            heading: 'Common Pitfalls to Avoid',
            points: [
              'Writing vague or ambiguous test steps',
              'Combining multiple test scenarios in one test case',
              'Assuming prior knowledge or context',
              'Neglecting edge cases and boundary conditions',
              'Failing to update test cases when requirements change',
              'Not considering different user roles and permissions'
            ]
          }
        ],
        conclusion: 'Effective test cases are the backbone of quality assurance. By following these guidelines and best practices, you can create test cases that are clear, maintainable, and provide comprehensive coverage of your application functionality.'
      }
    },
    {
      title: 'Manual vs Automation Testing: When to Use Each',
      excerpt: 'Understanding the strengths and limitations of manual and automated testing to make informed decisions for your project.',
      date: 'Nov 28, 2025',
      readTime: '7 min read',
      category: 'Testing Strategy',
      tags: ['Manual Testing', 'Automation', 'Strategy'],
      color: '#10b981',
      content: {
        introduction: 'Choosing between manual and automated testing is a critical decision that impacts project efficiency, cost, and quality. Understanding when to use each approach ensures optimal test coverage and resource utilization.',
        sections: [
          {
            heading: 'When to Use Manual Testing',
            points: [
              'Exploratory testing requiring human intuition and creativity',
              'Usability and user experience testing',
              'Ad-hoc testing and initial feature validation',
              'Tests that require human judgment or visual verification',
              'Short-term or one-time testing scenarios',
              'Testing complex user workflows with multiple variations',
              'Early-stage development with frequent UI changes'
            ]
          },
          {
            heading: 'When to Use Automation Testing',
            points: [
              'Regression testing for stable features',
              'Repetitive test scenarios executed frequently',
              'Data-driven testing with multiple input combinations',
              'Performance and load testing',
              'API and backend testing',
              'Cross-browser and cross-platform testing',
              'Continuous integration and deployment pipelines'
            ]
          },
          {
            heading: 'Hybrid Approach: Best of Both Worlds',
            points: [
              'Use automation for stable, repetitive tests',
              'Reserve manual testing for exploratory and usability testing',
              'Automate smoke tests for quick validation',
              'Manually test new features before automating',
              'Balance automation ROI with maintenance costs',
              'Continuously evaluate and adjust testing strategy'
            ]
          }
        ],
        conclusion: 'The key to successful testing is not choosing one over the other, but understanding when each approach provides the most value. A well-balanced testing strategy leverages both manual and automated testing to achieve comprehensive quality assurance.'
      }
    },
    {
      title: 'Bug Severity vs Priority: Understanding the Difference',
      excerpt: 'A comprehensive guide to properly classifying defects based on severity and priority for effective defect management.',
      date: 'Nov 10, 2025',
      readTime: '4 min read',
      category: 'Defect Management',
      tags: ['Bug Tracking', 'Defects', 'Best Practices'],
      color: '#ef4444',
      content: {
        introduction: 'Properly classifying defects by severity and priority is crucial for effective defect management. While these terms are often confused, they represent different aspects of a bug and require different considerations.',
        sections: [
          {
            heading: 'Bug Severity Levels',
            points: [
              'Critical: System crash, data loss, security breach - blocks testing',
              'High: Major functionality broken, no workaround available',
              'Medium: Feature partially working, workaround exists',
              'Low: Minor issues, cosmetic defects, minimal impact'
            ]
          },
          {
            heading: 'Bug Priority Levels',
            points: [
              'P1 (Urgent): Must be fixed immediately, blocks release',
              'P2 (High): Should be fixed in current release',
              'P3 (Medium): Can be fixed in next release',
              'P4 (Low): Fix when time permits, nice to have'
            ]
          },
          {
            heading: 'Key Differences',
            points: [
              'Severity is technical impact - how badly is the system affected',
              'Priority is business impact - how urgently does it need fixing',
              'High severity does not always mean high priority',
              'Low severity bugs can have high priority (e.g., CEO demo bug)',
              'Severity is set by QA, priority by product management',
              'Both factors together determine fix timeline'
            ]
          },
          {
            heading: 'Real-World Examples',
            points: [
              'High Severity, High Priority: Payment processing failure',
              'High Severity, Low Priority: Rare edge case crash',
              'Low Severity, High Priority: Typo on homepage',
              'Low Severity, Low Priority: Minor UI alignment issue'
            ]
          }
        ],
        conclusion: 'Understanding the distinction between severity and priority enables better resource allocation, clearer communication, and more effective defect management across development teams.'
      }
    },
    {
      title: 'API Testing Fundamentals with Postman',
      excerpt: 'Master the basics of API testing including request types, status codes, authentication, and response validation.',
      date: 'Oct 22, 2025',
      readTime: '8 min read',
      category: 'API Testing',
      tags: ['API', 'Postman', 'REST'],
      color: '#f59e0b',
      content: {
        introduction: 'API testing is essential for modern applications. Postman provides a powerful, user-friendly platform for testing REST APIs, validating responses, and automating API test suites.',
        sections: [
          {
            heading: 'HTTP Request Methods',
            points: [
              'GET: Retrieve data from server (read operation)',
              'POST: Send data to server (create operation)',
              'PUT: Update existing resource completely',
              'PATCH: Partial update of existing resource',
              'DELETE: Remove resource from server',
              'HEAD: Get headers without response body',
              'OPTIONS: Check available HTTP methods'
            ]
          },
          {
            heading: 'HTTP Status Codes',
            points: [
              '200 OK: Request successful',
              '201 Created: Resource created successfully',
              '400 Bad Request: Invalid request syntax',
              '401 Unauthorized: Authentication required',
              '403 Forbidden: Access denied',
              '404 Not Found: Resource does not exist',
              '500 Internal Server Error: Server-side error'
            ]
          },
          {
            heading: 'Essential Postman Features',
            points: [
              'Collections: Organize related API requests',
              'Environment Variables: Manage different environments',
              'Pre-request Scripts: Set up test data before request',
              'Tests: Validate response data and status codes',
              'Mock Servers: Simulate API responses',
              'Monitors: Schedule automated test runs',
              'Documentation: Auto-generate API documentation'
            ]
          },
          {
            heading: 'API Testing Best Practices',
            points: [
              'Test all HTTP methods for each endpoint',
              'Validate response status codes and headers',
              'Verify response schema and data types',
              'Test authentication and authorization',
              'Check error handling and edge cases',
              'Test with invalid and boundary data',
              'Automate repetitive API tests'
            ]
          }
        ],
        conclusion: 'Mastering API testing with Postman enables you to ensure reliable, secure, and performant APIs. Start with basic requests and gradually build comprehensive test suites for complete API coverage.'
      }
    },
    {
      title: 'Load Testing Basics: Getting Started with JMeter',
      excerpt: 'Introduction to performance testing concepts and practical guide to creating your first load test with Apache JMeter.',
      date: 'Oct 5, 2025',
      readTime: '10 min read',
      category: 'Performance Testing',
      tags: ['JMeter', 'Load Testing', 'Performance'],
      color: '#8b5cf6',
      content: {
        introduction: 'Load testing ensures your application can handle expected user traffic and identifies performance bottlenecks. Apache JMeter is a powerful open-source tool for comprehensive performance testing.',
        sections: [
          {
            heading: 'Performance Testing Types',
            points: [
              'Load Testing: Verify system behavior under expected load',
              'Stress Testing: Find breaking point by increasing load',
              'Spike Testing: Test sudden traffic increases',
              'Endurance Testing: Verify stability over extended period',
              'Scalability Testing: Measure system growth capacity'
            ]
          },
          {
            heading: 'JMeter Core Components',
            points: [
              'Test Plan: Container for all test elements',
              'Thread Group: Simulates virtual users',
              'Samplers: Define requests (HTTP, FTP, JDBC)',
              'Listeners: Collect and display test results',
              'Assertions: Validate response data',
              'Timers: Add delays between requests',
              'Config Elements: Set default values and variables'
            ]
          },
          {
            heading: 'Creating Your First Load Test',
            points: [
              'Define test objectives and success criteria',
              'Create Thread Group with user count and ramp-up',
              'Add HTTP Request samplers for endpoints',
              'Configure realistic think times with timers',
              'Add assertions to validate responses',
              'Add listeners for result analysis',
              'Run test and analyze performance metrics'
            ]
          },
          {
            heading: 'Key Performance Metrics',
            points: [
              'Response Time: Time to receive complete response',
              'Throughput: Requests processed per second',
              'Error Rate: Percentage of failed requests',
              'Concurrent Users: Number of simultaneous users',
              'Resource Utilization: CPU, memory, network usage',
              'Latency: Time to first byte received'
            ]
          }
        ],
        conclusion: 'JMeter provides comprehensive performance testing capabilities. Start with simple load tests, gradually add complexity, and always analyze results to identify and resolve performance issues before they impact users.'
      }
    },
    {
      title: 'Building a Selenium Automation Framework',
      excerpt: 'Step-by-step guide to creating a robust, scalable test automation framework using Selenium and Page Object Model.',
      date: 'Sep 18, 2025',
      readTime: '12 min read',
      category: 'Automation',
      tags: ['Selenium', 'Framework', 'POM'],
      color: '#06b6d4',
      content: {
        introduction: 'A well-designed automation framework is essential for maintainable, scalable test automation. This guide covers building a robust Selenium framework using Page Object Model and industry best practices.',
        sections: [
          {
            heading: 'Framework Architecture',
            points: [
              'Page Object Model: Separate page elements from test logic',
              'Test Layer: Contains test cases and test suites',
              'Utility Layer: Reusable helper functions',
              'Configuration Layer: Environment and test data management',
              'Reporting Layer: Test execution reports and logs',
              'Driver Management: Browser initialization and cleanup'
            ]
          },
          {
            heading: 'Page Object Model Benefits',
            points: [
              'Improved code maintainability and reusability',
              'Reduced code duplication across tests',
              'Easy to update when UI changes',
              'Better separation of concerns',
              'Enhanced readability and collaboration',
              'Simplified test case creation'
            ]
          },
          {
            heading: 'Essential Framework Features',
            points: [
              'Cross-browser testing support (Chrome, Firefox, Edge)',
              'Parallel test execution for faster results',
              'Screenshot capture on test failure',
              'Detailed HTML reports with test metrics',
              'Data-driven testing with external data sources',
              'Integration with CI/CD pipelines',
              'Configurable waits and timeouts'
            ]
          },
          {
            heading: 'Best Practices',
            points: [
              'Use explicit waits instead of implicit waits',
              'Implement proper exception handling',
              'Create atomic, independent test cases',
              'Use meaningful naming conventions',
              'Maintain clean, DRY code principles',
              'Version control with Git',
              'Regular framework maintenance and updates',
              'Document framework usage and guidelines'
            ]
          },
          {
            heading: 'Getting Started',
            points: [
              'Set up project structure with proper folders',
              'Install Selenium WebDriver and dependencies',
              'Create base page class with common methods',
              'Implement page classes for each application page',
              'Write test cases using page objects',
              'Configure test execution and reporting',
              'Integrate with CI/CD for automated execution'
            ]
          }
        ],
        conclusion: 'Building a solid automation framework requires careful planning and adherence to best practices. Start simple, iterate based on needs, and continuously refine your framework to support efficient, maintainable test automation.'
      }
    }
  ];

  return (
    <section id="blog" className="blog">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">QA Knowledge Hub</h2>
          <p className="section-subtitle">
            Insights, tutorials, and best practices in software quality assurance
          </p>
        </motion.div>

        <div className="blog-grid">
          {blogPosts.map((post, index) => (
            <motion.article
              key={index}
              className="blog-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
            >
              <div className="blog-header" style={{ borderTopColor: post.color }}>
                <span className="blog-category" style={{ backgroundColor: post.color }}>
                  {post.category}
                </span>
                <div className="blog-meta">
                  <span className="blog-date">
                    <FaClock /> {post.date}
                  </span>
                  <span className="blog-read-time">{post.readTime}</span>
                </div>
              </div>

              <div className="blog-content">
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>

                <div className="blog-tags">
                  <FaTag className="tag-icon" />
                  {post.tags.map((tag, idx) => (
                    <span key={idx} className="blog-tag">{tag}</span>
                  ))}
                </div>
              </div>

              <button className="blog-read-more" style={{ color: post.color }} onClick={() => setSelectedPost(post)}>
                Read More <FaArrowRight />
              </button>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedPost && (
          <motion.div
            className="blog-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              className="blog-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-modal" onClick={() => setSelectedPost(null)}>
                <FaTimes />
              </button>

              <div className="modal-header">
                <span className="modal-category" style={{ backgroundColor: selectedPost.color }}>
                  {selectedPost.category}
                </span>
                <h2>{selectedPost.title}</h2>
                <div className="modal-meta">
                  <span><FaClock /> {selectedPost.date}</span>
                  <span>{selectedPost.readTime}</span>
                </div>
              </div>

              <div className="modal-content">
                <div className="modal-section">
                  <p className="blog-introduction">{selectedPost.content.introduction}</p>
                </div>

                {selectedPost.content.sections.map((section, idx) => (
                  <div key={idx} className="modal-section">
                    <h3>{section.heading}</h3>
                    <ul className="blog-points-list">
                      {section.points.map((point, pointIdx) => (
                        <li key={pointIdx}>
                          <FaCheckCircle className="point-icon" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="modal-section">
                  <h3>Conclusion</h3>
                  <p className="blog-conclusion">{selectedPost.content.conclusion}</p>
                </div>

                <div className="modal-section">
                  <h3>Tags</h3>
                  <div className="modal-tags">
                    {selectedPost.tags.map((tag, idx) => (
                      <span key={idx} className="modal-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Blog;
